import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Help as HelpIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import AlertsDecisionGuide from './AlertsDecisionGuide';
import adminReservationService from '../../services/adminReservationService';

const AdminAlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAlerts();
  }, [selectedSeverity]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminReservationService.getAlerts({
        severity: selectedSeverity,
        limit: 100
      });
      
      console.log('🔍 Response from service:', response);
      
      // Vérification de sécurité pour éviter les erreurs
      if (response && response.success) {
        setAlerts(response.data?.alerts || []);
        setSummary(response.data?.summary || null);
      } else {
        setAlerts([]);
        setSummary(null);
        console.warn('Réponse inattendue du service:', response);
        setError('Réponse inattendue du serveur');
      }
    } catch (error) {
      console.error('Erreur chargement alertes:', error);
      setAlerts([]);
      setSummary(null);
      setError(error.message || 'Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAlerts();
    setRefreshing(false);
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ErrorIcon color="error" />;
      case 'high': return <WarningIcon color="warning" />;
      case 'medium': return <InfoIcon color="info" />;
      case 'low': return <CheckCircleIcon color="success" />;
      default: return <InfoIcon />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getProtectionColor = (level) => {
    switch (level) {
      case 'MAXIMUM': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'success';
      case 'NONE': return 'default';
      default: return 'default';
    }
  };

  const getResponsibilityIcon = (responsibility) => {
    if (!responsibility) return <PersonIcon />;
    if (responsibility.includes('OWNER')) return <PersonIcon color="error" />;
    if (responsibility.includes('RENTER')) return <PersonIcon color="warning" />;
    if (responsibility.includes('SYSTEM')) return <InfoIcon color="action" />;
    return <PersonIcon />;
  };

  const formatTimeOverdue = (alert) => {
    if (!alert) return 'Inconnu';
    
    if (alert.days_overdue && alert.days_overdue > 0) {
      return `${alert.days_overdue} jour(s)`;
    } else if (alert.hours_overdue && alert.hours_overdue > 0) {
      return `${alert.hours_overdue}h`;
    } else if (alert.hours_late && alert.hours_late > 0) {
      return `${alert.hours_late}h`;
    }
    return 'Récent';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header avec bouton du guide */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          🚨 Alertes de Réservations
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<HelpIcon />}
            onClick={() => setGuideOpen(true)}
            size="large"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              fontWeight: 'bold',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: 4
              }
            }}
          >
            Guide de Décision
          </Button>
          
          <Tooltip title="Actualiser les alertes">
            <IconButton 
              onClick={handleRefresh}
              disabled={refreshing}
              color="primary"
              size="large"
            >
              {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Message d'erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6">❌ Erreur de chargement</Typography>
          <Typography>{error}</Typography>
        </Alert>
      )}

      {/* Résumé des alertes */}
      {summary && summary.total_alerts !== undefined && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h3" color="error" fontWeight="bold">
                {summary.total_alerts || 0}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Alertes Actives
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Par Sévérité:</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Badge badgeContent={summary.by_severity?.critical || 0} color="error">
                  <Chip label="Critique" color="error" variant="outlined" />
                </Badge>
                <Badge badgeContent={summary.by_severity?.high || 0} color="warning">
                  <Chip label="Élevée" color="warning" variant="outlined" />
                </Badge>
                <Badge badgeContent={summary.by_severity?.medium || 0} color="info">
                  <Chip label="Moyenne" color="info" variant="outlined" />
                </Badge>
                <Badge badgeContent={summary.by_severity?.low || 0} color="success">
                  <Chip label="Faible" color="success" variant="outlined" />
                </Badge>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Filtres de sévérité */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filtrer par Sévérité:</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {['all', 'critical', 'high', 'medium', 'low'].map((severity) => (
            <Button
              key={severity}
              variant={selectedSeverity === severity ? 'contained' : 'outlined'}
              color={severity === 'all' ? 'primary' : getSeverityColor(severity)}
              onClick={() => setSelectedSeverity(severity)}
              size="small"
            >
              {severity === 'all' ? 'Toutes' : severity.charAt(0).toUpperCase() + severity.slice(1)}
            </Button>
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Liste des alertes */}
      {!alerts || alerts.length === 0 ? (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="h6">🎉 Aucune alerte détectée</Typography>
          <Typography>Toutes les réservations sont dans un état cohérent !</Typography>
        </Alert>
      ) : (
        <Box>
          <Typography variant="h6" gutterBottom>
            {alerts.length} Alerte{alerts.length > 1 ? 's' : ''} Détectée{alerts.length > 1 ? 's' : ''}
          </Typography>
          
          {alerts.map((alert, index) => {
            if (!alert || !alert.id) return null;
            
            return (
            <Card key={alert.id} sx={{ mb: 2, border: `2px solid`, borderColor: `${getSeverityColor(alert.severity || 'default')}.main` }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getSeverityIcon(alert.severity)}
                    <Box>
                      <Typography variant="h6" color={`${getSeverityColor(alert.severity || 'default')}.main`}>
                        {alert.title || 'Alerte sans titre'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Réservation #{alert.reservation_id || 'Inconnue'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      label={(alert.severity || 'default').toUpperCase()} 
                      color={getSeverityColor(alert.severity || 'default')}
                      size="small"
                    />
                    {alert.cas_type && (
                      <Chip 
                        label={alert.cas_type}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  {alert.description || 'Aucune description disponible'}
                </Typography>

                {/* Informations détaillées */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">RETARD</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatTimeOverdue(alert)}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getResponsibilityIcon(alert.responsibility)}
                      <Box>
                        <Typography variant="caption" color="text.secondary">RESPONSABILITÉ</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {alert.responsibility || 'Non définie'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">PAIEMENT</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {alert.payment_context || 'Non défini'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Protection et Actions recommandées */}
                {(alert.client_protection_level || alert.suggested_action) && (
                  <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      {alert.client_protection_level && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ShieldIcon color="primary" />
                          <Typography variant="body2" fontWeight="bold">
                            Protection Client:
                          </Typography>
                          <Chip 
                            label={alert.client_protection_level}
                            color={getProtectionColor(alert.client_protection_level)}
                            size="small"
                          />
                        </Box>
                      )}
                      
                      {alert.suggested_action && (
                        <Chip 
                          label={alert.suggested_action.replace(/_/g, ' ')}
                          color="primary"
                          variant="filled"
                          size="small"
                        />
                      )}
                    </Box>
                    
                    {/* Codes de sécurité si disponibles */}
                    {(alert.pickup_confirmed !== undefined || alert.return_confirmed !== undefined) && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        {alert.pickup_confirmed !== undefined && (
                          <Typography variant="body2">
                            🔐 Pickup: {alert.pickup_confirmed ? '✅ Confirmé' : '❌ Non confirmé'}
                          </Typography>
                        )}
                        {alert.return_confirmed !== undefined && (
                          <Typography variant="body2">
                            🔐 Retour: {alert.return_confirmed ? '✅ Confirmé' : '❌ Non confirmé'}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Client et Propriétaire */}
                {(alert.renter || alert.listing) && (
                  <Grid container spacing={2}>
                    {alert.renter && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">CLIENT</Typography>
                        <Typography variant="body2">
                          {alert.renter.name} - {alert.renter.email}
                        </Typography>
                      </Grid>
                    )}
                    
                    {alert.listing && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">ÉQUIPEMENT</Typography>
                        <Typography variant="body2">
                          {alert.listing.title}
                          {alert.listing.owner && ` - ${alert.listing.owner.name}`}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                )}

                {/* Actions */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    size="small"
                    onClick={() => {
                      // Redirection vers détail réservation
                      window.open(`/admin/reservations/${alert.reservation_id}`, '_blank');
                    }}
                  >
                    Voir Détails
                  </Button>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setGuideOpen(true)}
                  >
                    Consulter Guide
                  </Button>
                </Box>
              </CardContent>
            </Card>
            );
          })}
        </Box>
      )}

      {/* Modal du Guide de Décision */}
      <AlertsDecisionGuide 
        open={guideOpen} 
        onClose={() => setGuideOpen(false)} 
      />
    </Box>
  );
};

export default AdminAlertsPage;